import type { SiteDocument, Theme } from "@zirro/schema";
import type { Breakpoint } from "./types";

export const FONT_STACKS: Record<string, string> = {
  inter: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  roboto: "'Roboto', system-ui, -apple-system, sans-serif",
  "open-sans": "'Open Sans', system-ui, sans-serif",
  lato: "'Lato', system-ui, sans-serif",
  montserrat: "'Montserrat', system-ui, sans-serif",
  "dm-sans": "'DM Sans', system-ui, sans-serif",
  "space-grotesk": "'Space Grotesk', system-ui, sans-serif",
  "ibm-plex-sans": "'IBM Plex Sans', system-ui, sans-serif",
  "playfair-display": "'Playfair Display', Georgia, serif",
  merriweather: "'Merriweather', Georgia, serif",
  "source-serif-4": "'Source Serif 4', Georgia, serif",
  "libre-baskerville": "'Libre Baskerville', Georgia, serif",
};

export function resolveFontFamily(fontId?: unknown): string | undefined {
  if (typeof fontId !== "string" || !fontId.trim()) return undefined;
  return FONT_STACKS[fontId] ?? fontId;
}

const RADII: Record<Theme["radius"], string> = {
  none: "0px",
  sm: "4px",
  md: "8px",
  lg: "16px",
  full: "9999px",
};

const SPACING = { none: "0px", XS: "4px", SM: "8px", MD: "16px", LG: "32px", XL: "64px" } as const;

const COLOR_ROLES = [
  "primary",
  "secondary",
  "accent",
  "background",
  "surface",
  "text",
  "muted",
  "border",
] as const;

/** Canonical CSS custom properties for a theme (PRD §75, §138). */
export function themeToCssVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const role of COLOR_ROLES) {
    const value = theme.colors[role];
    if (value) vars[`--zirro-color-${role}`] = value;
  }
  vars["--zirro-font-heading"] =
    FONT_STACKS[theme.typography.headingFont] ?? FONT_STACKS.inter!;
  vars["--zirro-font-body"] =
    FONT_STACKS[theme.typography.bodyFont] ?? FONT_STACKS.inter!;
  vars["--zirro-radius"] = RADII[theme.radius];
  return vars;
}

export function spacingVar(size: keyof typeof SPACING): string {
  return SPACING[size];
}

/** Theme presets offered in the designer (PRD §139). */
export interface ThemePreset {
  id: string;
  label: string;
  theme: Theme;
}

const baseType = { headingFont: "inter", bodyFont: "inter" } as const;

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: "indigo",
    label: "Indigo",
    theme: {
      colors: {
        primary: "#4f46e5",
        secondary: "#0ea5e9",
        accent: "#f59e0b",
        background: "#ffffff",
        surface: "#f8fafc",
        text: "#0f172a",
        muted: "#64748b",
        border: "#e2e8f0",
      },
      typography: { ...baseType },
      radius: "md",
    },
  },
  {
    id: "slate",
    label: "Slate",
    theme: {
      colors: {
        primary: "#0f172a",
        secondary: "#334155",
        accent: "#0ea5e9",
        background: "#ffffff",
        surface: "#f1f5f9",
        text: "#0f172a",
        muted: "#64748b",
        border: "#e2e8f0",
      },
      typography: { ...baseType },
      radius: "sm",
    },
  },
  {
    id: "emerald",
    label: "Emerald",
    theme: {
      colors: {
        primary: "#059669",
        secondary: "#0d9488",
        accent: "#f59e0b",
        background: "#ffffff",
        surface: "#ecfdf5",
        text: "#064e3b",
        muted: "#6b7280",
        border: "#d1fae5",
      },
      typography: { ...baseType },
      radius: "lg",
    },
  },
  {
    id: "rose",
    label: "Rose",
    theme: {
      colors: {
        primary: "#e11d48",
        secondary: "#f43f5e",
        accent: "#f97316",
        background: "#ffffff",
        surface: "#fff1f2",
        text: "#1f2937",
        muted: "#6b7280",
        border: "#ffe4e6",
      },
      typography: {
        headingFont: "playfair-display",
        bodyFont: "inter",
      },
      radius: "md",
    },
  },
  {
    id: "midnight",
    label: "Midnight",
    theme: {
      colors: {
        primary: "#818cf8",
        secondary: "#38bdf8",
        accent: "#fbbf24",
        background: "#0f172a",
        surface: "#1e293b",
        text: "#f1f5f9",
        muted: "#94a3b8",
        border: "#334155",
      },
      typography: { ...baseType },
      radius: "lg",
    },
  },
];

export function getPreset(id: string): ThemePreset | undefined {
  return THEME_PRESETS.find((p) => p.id === id);
}

export interface ResolvedNodeProps {
  props: Record<string, unknown>;
  /** CSS that forces the active breakpoint's props (editor viewport preview). */
  forced: Record<string, string> | null;
}

/**
 * Resolves a node's props for a breakpoint (PRD §48–49):
 * base props, then tablet, then mobile overrides — deepest wins.
 * In editor viewport mode we also emit inline CSS for overridden props so a
 * single desktop-width DOM can preview narrower layouts without iframes.
 */
export function resolveNodeProps(
  node: {
    props: Record<string, unknown>;
    responsive?: { tablet?: Record<string, unknown>; mobile?: Record<string, unknown> };
  },
  breakpoint?: Breakpoint,
): ResolvedNodeProps {
  const tablet = node.responsive?.tablet ?? {};
  const mobile = node.responsive?.mobile ?? {};
  let props: Record<string, unknown> = { ...node.props };

  if (breakpoint === "tablet" || breakpoint === "mobile") {
    props = { ...props, ...tablet };
  }
  if (breakpoint === "mobile") {
    props = { ...props, ...mobile };
  }

  let forced: Record<string, string> | null = null;
  if (breakpoint && breakpoint !== "desktop") {
    forced = forcedCssFor(props);
  }
  return { props, forced };
}

function forcedCssFor(props: Record<string, unknown>): Record<string, string> | null {
  const css: Record<string, string> = {};
  if (typeof props.align === "string") css.textAlign = props.align;
  if (props.width === "full") css.maxWidth = "100%";
  if (props.width === "contained") css.maxWidth = props.customMaxWidth ? String(props.customMaxWidth) : "1200px";
  if (props.maxWidth) css.maxWidth = String(props.maxWidth);
  if (props.fontSize) css.fontSize = fontSizeValue(props.fontSize);
  if (props.size) css.fontSize = fontSizeValue(props.size);
  if (props.weight) css.fontWeight = String(props.weight);
  if (props.lineHeight) css.lineHeight = String(props.lineHeight);
  if (props.letterSpacing) css.letterSpacing = String(props.letterSpacing);
  if (props.textTransform) css.textTransform = String(props.textTransform);
  if (props.textDecoration) css.textDecoration = String(props.textDecoration);
  if (props.fontFamily) css.fontFamily = resolveFontFamily(props.fontFamily) ?? String(props.fontFamily);
  if (props.textColor) css.color = String(props.textColor);
  if (props.direction === "horizontal") {
    css.display = "flex";
    css.flexDirection = "row";
    css.flexWrap = "wrap";
  } else if (props.direction === "vertical") {
    css.display = "flex";
    css.flexDirection = "column";
  }
  if (typeof props.gap === "string") css.gap = SPACING[props.gap as keyof typeof SPACING] ?? props.gap;
  if (typeof props.padding === "string") css.padding = SPACING[props.padding as keyof typeof SPACING] ?? props.padding;
  if (props.marginTop) css.marginTop = String(props.marginTop);
  if (props.marginBottom) css.marginBottom = String(props.marginBottom);
  return Object.keys(css).length ? css : null;
}

const FONT_SIZES: Record<string, string> = {
  xs: "12px",
  sm: "14px",
  base: "16px",
  lg: "18px",
  xl: "20px",
  "2xl": "24px",
  "3xl": "30px",
  "4xl": "36px",
  "5xl": "48px",
};

export function fontSizeValue(size: unknown): string {
  if (typeof size === "number") return `${size}px`;
  if (typeof size === "string") {
    const s = size.trim().toLowerCase();
    if (FONT_SIZES[s]) return FONT_SIZES[s];
    if (s.endsWith("px") || s.endsWith("rem") || s.endsWith("em") || s.endsWith("%")) return size;
    const num = Number(s);
    if (!isNaN(num) && num > 0) return `${num}px`;
  }
  return "16px";
}

/** Wraps children of a document with theme CSS variables (PRD §140). */
export function documentStyleProps(
  document: SiteDocument,
): Record<string, string> {
  return themeToCssVars(document.theme);
}
